/**
 * watchouts.tsx (360-B, 2026-09-08 — Seth picked Direction B + the Watch-outs
 * editor from the idea-session canvas).
 *
 * ONE list per customer of the must-not-miss items, read by the customer
 * page's rail (WatchOutsBlock), edited in ONE place (WatchOutsEditor), and
 * shared with the board / tech app through `useWatchouts`. The list is:
 *
 *   · AUTOMATIC rows Pegasus derives (Do Not Service, marked inactive, plan
 *     expiring ≤ 45 d / lapsed) — shown, never editable, they clear themselves.
 *   · STORED rows in `extracted_signal` — typed by the office (source manual)
 *     or found in old notes and KEPT (status confirmed). `hidden_at` = switched
 *     off: still on file, not on the record.
 *   · FOUND rows (status proposed) — Pegasus's reads of old notes. The rail
 *     shows the confident ones (≥ 0.7) dashed with ✓ / ✕; the editor lists
 *     every one under "Pegasus found these" until someone keeps or rejects it.
 *
 * Severity order is the display order everywhere: blocking (rose) → routing
 * (amber) → access (sky) → note (slate).
 */
import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Ban, Users, UserX, UserRound, ShieldCheck, Clock, PhoneCall, KeyRound, Dog, Sun, Building2, Info, Star, Package, Quote, type LucideIcon, BellRing } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";
import { useCustomerSignals, type CustomerSignal } from "./api";
import { useCustomerAgreementRenewal, useMembershipStatus } from "@/features/agreements/api";
import { useCustomerFollowups, dueLabel, todayISO } from "@/features/followups/followups";
import { useAllActiveTechs } from "@/features/board/api";

export type WatchSeverity = "blocking" | "routing" | "access" | "info";
export type WatchFamily = "Caution" | "Routing" | "Access" | "Note" | "Automatic";

export interface WatchKind {
  /** Chip label in the editor: "Routing · paired with". */
  family: WatchFamily;
  label: string;
  icon: LucideIcon;
  severity: WatchSeverity;
  /** The "Add a watch-out" chip text; kinds without one are not offered for adding. */
  addLabel?: string;
  placeholder?: string;
}

/** Every signal type the extractor writes, in the vocabulary the office uses. */
export const WATCH_KINDS: Record<string, WatchKind> = {
  hazard: { family: "Caution", label: "hazard", icon: AlertTriangle, severity: "blocking", addLabel: "Caution / hazard", placeholder: "Two big dogs — text before you arrive" },
  pairing: { family: "Routing", label: "paired with", icon: Users, severity: "routing", addLabel: "Paired with…", placeholder: "Paired with 8500 97 1/2 Street W — book the two together" },
  tech_pref: { family: "Routing", label: "requests a tech", icon: UserRound, severity: "routing", addLabel: "Don't send / requests a tech", placeholder: "Requests Adam — or: Don't send Ethan" },
  skill_req: { family: "Routing", label: "crew requirement", icon: Star, severity: "routing", addLabel: "Needs a senior tech", placeholder: "Senior tech only — rooftop units" },
  window_pref: { family: "Routing", label: "time window", icon: Clock, severity: "routing", addLabel: "Time window", placeholder: "Mornings only — leaves for work at 1" },
  seasonal: { family: "Routing", label: "away for the season", icon: Sun, severity: "routing", addLabel: "Away for the season", placeholder: "In Arizona November through April" },
  vip: { family: "Routing", label: "VIP", icon: Star, severity: "routing", addLabel: "VIP", placeholder: "Seth's neighbor — white-glove" },
  access_instruction: { family: "Access", label: "call ahead / getting in", icon: PhoneCall, severity: "access", addLabel: "Call ahead / getting in", placeholder: "Call 952-941-3259 on the way — side door" },
  access_code: { family: "Access", label: "code", icon: KeyRound, severity: "access", addLabel: "Gate / lockbox code", placeholder: "Garage keypad 0645" },
  animal: { family: "Access", label: "animal", icon: Dog, severity: "access", addLabel: "Dog / animal", placeholder: "Two friendly labs — keep the gate shut" },
  business_hours: { family: "Access", label: "hours", icon: Building2, severity: "access", addLabel: "Business hours", placeholder: "Open 8–4, closed Fridays" },
  caller_verification: { family: "Note", label: "who may authorize", icon: ShieldCheck, severity: "info", addLabel: "Who may authorize work", placeholder: "Only Barb approves work over $500" },
  consumable: { family: "Note", label: "consumable", icon: Package, severity: "info", addLabel: "Filter / consumable", placeholder: "Furnace filter is a 20x25x1" },
  heads_up: { family: "Note", label: "heads-up", icon: Info, severity: "info", addLabel: "Plain note", placeholder: "Type it the way you'd say it on the phone" },
  nickname: { family: "Note", label: "goes by", icon: Quote, severity: "info", addLabel: "Goes by", placeholder: "Barb" },
};

/** The chips offered under "Add a watch-out", in the order Seth's mock had them. */
export const ADD_KINDS: string[] = [
  "hazard", "pairing", "tech_pref", "window_pref", "seasonal", "access_instruction", "access_code",
  "animal", "business_hours", "skill_req", "vip", "caller_verification", "consumable", "nickname", "heads_up",
];

/** Types that are NOT watch-outs (ownership history lives on its own card). */
const EXCLUDED_TYPES = new Set(["prior_owner"]);

export const SEVERITY_ORDER: Record<WatchSeverity, number> = { blocking: 0, routing: 1, access: 2, info: 3 };
/** The rail shows a found-in-notes row only when the extractor was this sure. */
export const MIN_CONF = 0.7;

export interface WatchItem {
  key: string;
  severity: WatchSeverity;
  /** A signal type, or one of the automatic kinds. */
  kind: string;
  icon: LucideIcon;
  /** The sentence, the way you'd say it on the phone. */
  text: ReactNode;
  signal?: CustomerSignal;
  /** Set on automatic rows — where the item comes from and why it can't be edited. */
  auto?: string;
  proposed: boolean;
}

export function kindOf(type: string): WatchKind {
  return WATCH_KINDS[type] ?? { family: "Note", label: type.replace(/_/g, " "), icon: Info, severity: "info" };
}

export const stripStars = (v: string) => v.replace(/^\*+\s*|\s*\*+$/g, "").trim();
const fmtPlanDate = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString("en-US", d.getFullYear() === new Date().getFullYear() ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
};

export function pretty10(s: string): string {
  const d = s.replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s;
}

/** "Paired with 8500 97 1/2 Street W" → the customer at that address, when Pegasus can find exactly one. */
export function usePairedCustomer(address: string | null) {
  const needle = (address ?? "").replace(/^paired with\s+/i, "").replace(/\*/g, "").trim();
  return useQuery({
    queryKey: ["paired_customer", needle],
    enabled: needle.length >= 6,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ custno: string; name: string } | null> => {
      const street = needle.replace(/,.*$/, "").trim();
      const { data } = await supabase.from("mirror_location").select("custno, locname, add1")
        .ilike("add1", `${street}%`).limit(3);
      const rows = (data ?? []) as { custno: string; locname: string | null; add1: string | null }[];
      const distinct = [...new Set(rows.map((r) => r.custno))];
      if (distinct.length !== 1) return null;
      const { data: c } = await supabase.from("mirror_customer").select("fullname").eq("custno", distinct[0]).maybeSingle();
      return { custno: distinct[0], name: displayCustomerName(rows[0].locname, (c as { fullname?: string } | null)?.fullname ?? null) };
    },
  });
}

/**
 * The sentence for one stored / found row. Shared by the rail and the editor
 * so a watch-out reads the same everywhere.
 */
export function sentenceFor(s: CustomerSignal, paired?: { custno: string; name: string } | null): { icon: LucideIcon; severity: WatchSeverity; text: ReactNode } {
  const k = kindOf(s.signal_type);
  const v = stripStars(s.value);
  switch (s.signal_type) {
    case "hazard":
      return { icon: AlertTriangle, severity: "blocking", text: <><b>Caution:</b> {v}</> };
    case "pairing": {
      const addr = v.replace(/^paired with\s+/i, "");
      return {
        icon: Users, severity: "routing",
        text: <><b>Paired with {addr}</b> — book the two visits together{paired ? <> · <Link to={`/customers/${paired.custno}`} className="font-semibold text-brand-700 underline-offset-2 hover:underline">{paired.name} #{paired.custno}</Link></> : null}</>,
      };
    }
    case "tech_pref": {
      const exclude = !!(s.detail as { exclude?: boolean } | null)?.exclude || /^(not|no|don'?t)\b/i.test(v);
      const who = v.replace(/^(not|no|don'?t send)\s+/i, "");
      return { icon: exclude ? UserX : UserRound, severity: "routing", text: exclude ? <><b>Don't send {who}</b></> : <><b>Requests {who}</b></> };
    }
    case "skill_req":
      return { icon: Star, severity: "routing", text: <><b>{v}</b> for this account</> };
    case "window_pref":
      return { icon: Clock, severity: "routing", text: <>Book <b>{v}</b></> };
    case "seasonal":
      return { icon: Sun, severity: "routing", text: <><b>Away for the season</b> — {v}</> };
    case "vip":
      return { icon: Star, severity: "routing", text: <><b>VIP</b>{v && !/^vip$/i.test(v) ? ` — ${v}` : ""}</> };
    case "access_instruction": {
      const phone = v.match(/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/)?.[0];
      if (phone && /call|text|phone/i.test(s.source_text ?? v)) {
        const rest = v.replace(phone, "").replace(/^[\s,·-]+|[\s,·-]+$/g, "").replace(/^(call|text|phone)\s*(ahead|first|on the way)?\s*/i, "");
        return { icon: PhoneCall, severity: "access", text: <><b>Call {pretty10(phone)}</b> on the way{rest ? ` · ${rest}` : ""}</> };
      }
      return { icon: PhoneCall, severity: "access", text: <>{v}</> };
    }
    case "access_code":
      return { icon: KeyRound, severity: "access", text: <>Gate / lockbox code <b className="tabular-nums">{v}</b></> };
    case "animal":
      return { icon: Dog, severity: "access", text: <>{/\bat the home\b/i.test(v) ? v : `${v} at the home`}</> };
    case "business_hours":
      return { icon: Building2, severity: "access", text: <>Hours: <b>{v}</b></> };
    case "caller_verification":
      return { icon: ShieldCheck, severity: "info", text: <>{v}</> };
    case "consumable":
      return { icon: Package, severity: "info", text: <>{v}</> };
    case "nickname":
      return { icon: Quote, severity: "info", text: <>Goes by <b>{v}</b> — pinned next to the name</> };
    case "heads_up": {
      // A bare phone number filed as a heads-up is really "call this number" (seen on 0007266).
      if (/^\+?[\d\s().-]{10,16}$/.test(v)) return { icon: PhoneCall, severity: "access", text: <><b>Call {pretty10(v)}</b>{/on (the )?way|before|ahead/i.test(s.source_text ?? "") ? " on the way" : ""}</> };
      return { icon: Info, severity: "info", text: <>{v}</> };
    }
    default:
      return { icon: k.icon, severity: k.severity, text: <>{v}</> };
  }
}

export interface WatchoutsArgs {
  custno: string;
  locno?: string | null;
  /** Derived Do Not Service (Pegasus mark OR the ESC credit rating). */
  dns?: boolean;
  dnsReason?: string | null;
  /** Set (possibly "") when the account is marked inactive in Pegasus. */
  inactiveReason?: string | null;
}

export interface Watchouts {
  /** What the RECORD shows, in severity order: automatic + confirmed-and-on + confident finds. */
  items: WatchItem[];
  /** Every stored row that is on the record (confirmed, not switched off). */
  showing: CustomerSignal[];
  /** Confirmed rows the office switched off. */
  hidden: CustomerSignal[];
  /** Pegasus's finds still waiting for keep / not right (all confidences). */
  found: CustomerSignal[];
  /** Rejected finds and removed rows — recoverable. */
  dismissed: CustomerSignal[];
  isLoading: boolean;
}

/**
 * The one hook. Location-scoped: a row filed against a property shows only
 * while viewing that property; location-less rows travel with the account.
 */
export function useWatchouts({ custno, locno, dns = false, dnsReason, inactiveReason }: WatchoutsArgs): Watchouts {
  const { data: signals, isLoading } = useCustomerSignals(custno);
  const { data: renewal } = useCustomerAgreementRenewal(custno);
  // MEMBERSHIPS-03: the plan's lifecycle decides the plan line — "ended ·
  // checks owed" is not a win-back, it's a promise still to keep.
  const { data: life } = useMembershipStatus(custno || undefined);
  // FOLLOW-UP-01: open follow-ups ride on the same list — the rail, the
  // editor and the phone chips all see "Follow-up due: …" without a new surface.
  const { data: followups } = useCustomerFollowups(custno || undefined);
  const { data: techs } = useAllActiveTechs();

  const scoped = useMemo(() => (signals ?? []).filter(
    (s) => !!s.value && !s.archived_at && !EXCLUDED_TYPES.has(s.signal_type)
      && (!s.locno || !locno || s.locno === locno),
  ), [signals, locno]);

  const showing = useMemo(() => scoped.filter((s) => s.status === "confirmed" && !s.hidden_at), [scoped]);
  const hidden = useMemo(() => scoped.filter((s) => s.status === "confirmed" && !!s.hidden_at), [scoped]);
  const found = useMemo(() => [...scoped.filter((s) => s.status === "proposed")].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)), [scoped]);
  const dismissed = useMemo(() => scoped.filter((s) => s.status === "dismissed"), [scoped]);

  const pairingRow = showing.find((s) => s.signal_type === "pairing") ?? found.find((s) => s.signal_type === "pairing" && (s.confidence ?? 0) >= MIN_CONF) ?? null;
  const paired = usePairedCustomer(pairingRow?.value ?? null);

  const items = useMemo<WatchItem[]>(() => {
    const out: WatchItem[] = [];
    if (dns) out.push({ key: "dns", kind: "dns", severity: "blocking", icon: Ban, proposed: false, auto: "from the account — change it under Edit ▾ on the name", text: <><b>Do Not Service</b>{dnsReason ? ` — ${dnsReason}` : " — no new dispatches on this account"}</> });
    if (inactiveReason !== undefined && inactiveReason !== null) out.push({ key: "inactive", kind: "inactive", severity: "blocking", icon: Ban, proposed: false, auto: "from the account — change it under Edit ▾ on the name", text: <><b>Marked inactive</b>{inactiveReason ? ` — ${inactiveReason}` : " — hidden from search"}</> });
    const stored = [...showing, ...found.filter((s) => (s.confidence ?? 0) >= MIN_CONF)];
    for (const s of stored) {
      // The rail is for dispatching; "goes by" pins next to the name instead.
      if (s.signal_type === "nickname") continue;
      const sen = sentenceFor(s, s.signal_type === "pairing" ? paired.data : undefined);
      out.push({ key: s.id, kind: s.signal_type, severity: sen.severity, icon: sen.icon, text: sen.text, signal: s, proposed: s.status === "proposed" });
    }
    if (renewal && renewal.days_left >= 0 && renewal.days_left <= 45) out.push({ key: "renewal", kind: "renewal", severity: "routing", icon: ShieldCheck, proposed: false, auto: "from the plan — not editable, clears itself", text: <><b>Plan expires in {renewal.days_left} day{renewal.days_left === 1 ? "" : "s"}</b> — bring the renewal up at the visit</> });
    if (life?.status === "ended") {
      const n = life.checks_owed;
      out.push({ key: "ended", kind: "ended", severity: "routing", icon: ShieldCheck, proposed: false, auto: "from the plan — not editable, clears itself",
        text: <><b>Plan ended {fmtPlanDate(life.expire_date)} — still owes {n === 1 ? "a check" : `${n} checks`}</b>{life.owed_labels ? ` (${life.owed_labels})` : ""}{life.owed_until ? ` · use by ${fmtPlanDate(life.owed_until)}` : ""} · renewal due</> });
    } else if (life?.status === "fulfilled") {
      // MEMBERSHIP-ESC-FULFILLED: every check used, paid through the date — a plan
      // visit booked now would be an unpaid one; the call is the renewal.
      out.push({ key: "fulfilled", kind: "fulfilled", severity: "routing", icon: ShieldCheck, proposed: false, auto: "from the plan — not editable, clears itself",
        text: <><b>Plan checks all used — paid through {fmtPlanDate(life.expire_date)}</b> · no plan visit left to book · bring the renewal up</> });
    } else if (renewal && renewal.days_left < 0 && renewal.days_left >= -365 && life?.status !== "renewed" && life?.status !== "active" && life?.status !== "expiring") {
      out.push({ key: "lapsed", kind: "lapsed", severity: "routing", icon: ShieldCheck, proposed: false, auto: "from the plan — not editable, clears itself", text: <><b>Plan expired {Math.abs(renewal.days_left)} days ago</b> — win-back window</> });
    }
    // FOLLOW-UP-01: due (or overdue) follow-ups are routing-level; future ones
    // are notes — both read like a person talking, with who's handling it.
    const today = todayISO();
    for (const f of followups ?? []) {
      // the agreement renewal follow-up already shows above as the plan line
      if (f.status !== "open" || f.source === "agreement") continue;
      const dl = dueLabel(f);
      const due = dl.tone === "overdue" || dl.tone === "today";
      const who = f.assigned_empno ? (techs ?? []).find((t) => t.empno === f.assigned_empno) : null;
      const whoName = who ? ((who.firstname ?? "").trim() || (who.empname ?? "").trim().split(/\s+/)[0] || f.assigned_empno) : null;
      const when = f.snoozed_until && f.snoozed_until > today ? `snoozed until ${fmtPlanDate(f.snoozed_until)}` : due ? dl.text : `due ${fmtPlanDate(f.due_date)}`;
      out.push({
        key: `fu:${f.id}`, kind: "followup", severity: due ? "routing" : "info", icon: BellRing, proposed: false,
        auto: "from Follow-ups — clear it on Today (or on the tech's phone when he's handling it)",
        text: <><b>Follow-up{due ? " due" : ""}:</b> {f.title}{f.details ? ` — ${f.details}` : ""} · {when}{whoName ? ` · ${whoName} handles it` : " · the office handles it"}{f.repeat_months ? ` · repeats every ${f.repeat_months === 12 ? "year" : f.repeat_months === 24 ? "2 years" : `${f.repeat_months} months`}` : ""}</>,
      });
    }
    // Stable: severity first, then confirmed before found, then as stored.
    return out
      .map((it, i) => ({ it, i }))
      .sort((a, b) => SEVERITY_ORDER[a.it.severity] - SEVERITY_ORDER[b.it.severity] || Number(a.it.proposed) - Number(b.it.proposed) || a.i - b.i)
      .map(({ it }) => it);
  }, [dns, dnsReason, inactiveReason, showing, found, renewal, paired.data, life, followups, techs]);

  return { items, showing, hidden, found, dismissed, isLoading };
}

// ---------------------------------------------------------------------------
// Writes — all against extracted_signal (authenticated RLS + zzaudit).
// ---------------------------------------------------------------------------

/** Switch a confirmed row off (hidden from the record, the board and the tech app) or back on. */
export function useSetSignalHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; hidden: boolean }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("extracted_signal").update({
        hidden_at: v.hidden ? new Date().toISOString() : null,
        hidden_by: v.hidden ? (u?.user?.email ?? null) : null,
        updated_at: new Date().toISOString(),
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer_signals"] }),
  });
}

/** Re-file a row: which property it applies to (null = every property) and / or what kind it is. */
export function useRefileSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; locno?: string | null; signal_type?: string }) => {
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (v.locno !== undefined) patch.locno = v.locno;
      if (v.signal_type) patch.signal_type = v.signal_type;
      const { error } = await supabase.from("extracted_signal").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer_signals"] }),
  });
}

/** "Added by Seth · Mar 2024" / "found in notes Sep 15, 2025 · kept by Julie Sep 8" — the row's provenance line. */
export function provenance(s: CustomerSignal): string {
  const who = (e: string | null) => (e ? e.replace(/@.*$/, "").replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null);
  const when = (iso: string | null | undefined, style: "month" | "day" = "day") => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return style === "month" ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  if (s.source === "manual") return ["added", who(s.confirmed_by) ? `by ${who(s.confirmed_by)}` : null, when(s.confirmed_at ?? s.created_at, "month")].filter(Boolean).join(" ");
  // The note's own date isn't stored (only its text) — say where it came from, not when.
  const found = "found in your notes";
  if (s.status === "confirmed") return [found, s.confirmed_by || s.confirmed_at ? `kept${who(s.confirmed_by) ? ` by ${who(s.confirmed_by)}` : ""}${when(s.confirmed_at) ? ` ${when(s.confirmed_at)}` : ""}` : null].filter(Boolean).join(" · ");
  return found;
}
